import { useState, useEffect } from 'react';
import { useAuth } from './useAuth';

export interface LessonProgress {
  lessonId: string;
  lessonTitle: string;
  grade: string;
  completedAt: string;
}

interface ProgressData {
  [userId: string]: {
    lessons: LessonProgress[];
    lastActivity: string;
  };
}

const STORAGE_KEY = 'math_mastery_progress';

export const useProgress = () => {
  const { user } = useAuth();
  const [progress, setProgress] = useState<LessonProgress[]>([]);
  const [loading, setLoading] = useState(true);

  // Load progress from localStorage
  useEffect(() => {
    if (!user) {
      setProgress([]);
      setLoading(false);
      return;
    }

    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      const data: ProgressData = stored ? JSON.parse(stored) : {};
      const userProgress = data[user.id]?.lessons || [];
      setProgress(userProgress);
    } catch (error) {
      console.error('Error loading progress:', error);
      setProgress([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Mark a lesson as complete
  const markComplete = (lessonId: string, lessonTitle: string, grade: string) => {
    if (!user) return;

    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      const data: ProgressData = stored ? JSON.parse(stored) : {};
      
      if (!data[user.id]) {
        data[user.id] = { lessons: [], lastActivity: new Date().toISOString() };
      }

      // Check if already completed
      const exists = data[user.id].lessons.some(l => l.lessonId === lessonId);
      if (exists) return;

      // Add new completion
      const newLesson: LessonProgress = {
        lessonId,
        lessonTitle,
        grade,
        completedAt: new Date().toISOString()
      };

      data[user.id].lessons.push(newLesson);
      data[user.id].lastActivity = new Date().toISOString();

      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      setProgress(data[user.id].lessons);
    } catch (error) {
      console.error('Error marking lesson complete:', error);
    }
  };

  // Check if a lesson is completed
  const isCompleted = (lessonId: string): boolean => {
    return progress.some(l => l.lessonId === lessonId);
  };

  // Get progress by grade
  const getGradeProgress = (grade: string) => {
    return progress.filter(l => l.grade === grade);
  };

  // Get total completed lessons
  const getTotalCompleted = (): number => {
    return progress.length;
  };

  // Get last activity date
  const getLastActivity = (): string | null => {
    if (!user) return null;
    
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      const data: ProgressData = stored ? JSON.parse(stored) : {};
      return data[user.id]?.lastActivity || null;
    } catch {
      return null;
    }
  };

  // Clear all progress (for testing)
  const clearProgress = () => {
    if (!user) return;
    
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      const data: ProgressData = stored ? JSON.parse(stored) : {};
      delete data[user.id];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      setProgress([]);
    } catch (error) {
      console.error('Error clearing progress:', error);
    }
  };

  return {
    progress,
    loading,
    markComplete,
    isCompleted,
    getGradeProgress,
    getTotalCompleted,
    getLastActivity,
    clearProgress
  };
};